import { useEffect, useState } from 'react';
import joi from 'joi';
import { objectToArray, objectMap } from 'helpers/data';

const fillDefaultData = (schema: any) => (schema ? objectMap(schema, (item: any) => item.value) : null);
const fillDefaultPristine = (schema: any) => (schema ? objectMap(schema, () => true) : null);
const fillDefaultErrors = (schema: any) => (schema ? objectMap(schema, () => '') : null);

export default (schema?: any) => {
  const [extraParams, setExtraParams] = useState<any>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [form, setForm] = useState<any>({
    valid: false,
    dirtyError: false,
    data: fillDefaultData(schema),
    pristine: fillDefaultPristine(schema),
    errors: fillDefaultErrors(schema),
    schema
  });

  const resetForm = () => {
    setForm({
      valid: false,
      dirtyError: false,
      pristine: fillDefaultPristine(schema),
      data: fillDefaultData(schema),
      errors: fillDefaultErrors(schema),
      schema
    });
  };

  const blurred = (key: string) => () => {
    // setForm({ ...form, pristine: { ...form.pristine, [key]: false } });
    setForm((prev: any) => ({ ...prev, pristine: { ...prev.pristine, [key]: false } }));
  };

  const setDirtyError = (dirtyError: boolean) => {
    setForm((prev: any) => ({ ...prev, dirtyError }));
  };

  const setSchema = (schema: any) => {
    setForm((prevForm: any) => ({ ...prevForm, schema }));
  };

  const setFormData = (key: string) => (value: any) => {
    setForm((prevForm: any) => ({
      ...prevForm,
      data: { ...prevForm.data, [key]: value }
    }));
  };

  const setFullForm = (data: any) => {
    setForm((prevForm: any) => ({ ...prevForm, data: { ...prevForm.data, ...data } }));
  };

  const setFullSchema = (schema: any, data: any) => {
    setForm((prevForm: any) => ({ ...prevForm, data: { ...prevForm.data, ...data }, schema }));
  };

  const changeSchema = (newSchema: any, newData: any) => {
    setForm({
      valid: false,
      dirtyError: false,
      data: newData,
      pristine: fillDefaultPristine(newSchema),
      errors: fillDefaultErrors(newSchema),
      schema: newSchema
    });
  };

  const validate = () => {
    setForm((prevForm: any) => {
      const { schema, data } = prevForm;
      const errors: any = fillDefaultErrors(schema);
      const validator = joi
        .object(objectToArray(schema).reduce((acc, cur) => ({ ...acc, [cur.id]: cur.joi }), {}))
        .options({ abortEarly: false });
      const { details = [] } = { ...validator.validate(data).error };
      let isFormValid = true;

      // uncomment the line bellow to check the messages key needed for custom error messages
      // console.log(details);

      for (let key of Object.keys(schema)) {
        const e = details.filter(item => item.path.includes(key));
        const hasError = e.length > 0;

        if (hasError) {
          isFormValid = false;
          errors[key] = e[0].message;
        }
      }

      return { ...prevForm, errors, valid: isFormValid };
    });
  };

  useEffect(() => {
    if (form.data) {
      validate();
    }
  }, [form.data]);

  return {
    setExtraParams,
    setSchema,
    setFormData,
    setFullForm,
    resetForm,
    setIsSubmitting,
    setFullSchema,
    changeSchema,
    isSubmitting,
    extraParams,
    schema: form.schema,
    valid: form.valid && !form.dirtyError,
    data: form.data,
    pristine: form.pristine,
    errors: form.errors,
    setDirtyError,
    blurred
  };
};
